import { EVENTS, MAX_EVENT_NAME, MAX_PARAMS, MAX_PARAM_NAME, MAX_PARAM_VALUE } from "./events";

// Common analytics entry point for the web app (GA4 via gtag):
//   import { analytics, EVENTS } from "./analytics";
//   analytics.track(EVENTS.ADD_TO_CART, { item_id, value, currency: "INR" });
//
// - Disabled (no-op, no script loaded) unless REACT_APP_GA_MEASUREMENT_ID is set at build time.
// - Consent Mode v2: analytics_storage starts denied and is granted only after the user accepts
//   the banner. Ad storage / ad user data / ad personalization are always denied (no ads).
// - user_id is the opaque user cuid only. No email, name, phone, free text (PII is dropped below).
// - Never throws.

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const CONSENT_KEY = "ota_analytics_consent"; // "granted" | "denied"

const PII_KEY = /email|phone|name|address|password|token|secret|dob|birth|lat$|lng$|latitude|longitude/i;
const EMAIL_VALUE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const PHONE_VALUE = /^\+?\d[\d\s-]{8,}$/;

let initialized = false;

const enabled = () => !!MEASUREMENT_ID && typeof window !== "undefined";

// Staff tools (admin, gate scanner, shop owner portal) are never tracked and never show the
// consent banner: they are used by staff at work, the gate scanner is event-critical, and
// staff traffic would pollute the public numbers.
const STAFF_PREFIXES = ["/admin", "/event-scanner", "/shop-owner"];
const isStaffPage = () => typeof window !== "undefined" && STAFF_PREFIXES.some((p) => window.location.pathname.startsWith(p));

// gtag.js only understands the real `arguments` object, not an array.
function gtag() {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export function sanitizeParams(params = {}) {
  const out = {};
  for (const [key, value] of Object.entries(params)) {
    if (Object.keys(out).length >= MAX_PARAMS) break;
    if (value === undefined || value === null) continue;
    if (key.length > MAX_PARAM_NAME || PII_KEY.test(key)) continue;
    if (typeof value === "string") {
      if (EMAIL_VALUE.test(value) || PHONE_VALUE.test(value)) continue;
      out[key] = value.slice(0, MAX_PARAM_VALUE);
    } else if (["number", "boolean", "object"].includes(typeof value)) {
      out[key] = value;
    }
  }
  return out;
}

// Which part of the web app a path belongs to; lets reports separate staff from the public.
export function sectionForPath(pathname = "") {
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/shop-owner")) return "shop_owner";
  if (pathname.startsWith("/shop")) return "shop";
  return "public";
}

const readConsent = () => {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
};

const consentArg = (granted) => ({
  analytics_storage: granted ? "granted" : "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
});

function init() {
  if (initialized || !enabled()) return;
  initialized = true;
  try {
    window.dataLayer = window.dataLayer || [];
    // Consent default must be set before the config call.
    gtag("consent", "default", consentArg(readConsent() === "granted"));
    gtag("js", new Date());
    // Page views are sent by <RouteTracker/> (single page app), not automatically.
    gtag("config", MEASUREMENT_ID, { send_page_view: false, allow_google_signals: false });
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
    document.head.appendChild(script);
  } catch {
    // analytics must never break the app
  }
}

function track(name, params = {}) {
  try {
    if (!enabled() || isStaffPage()) return;
    init();
    if (!name || name.length > MAX_EVENT_NAME) {
      if (process.env.NODE_ENV !== "production") console.warn("[analytics] bad event name", name);
      return;
    }
    const clean = sanitizeParams(params);
    if (process.env.NODE_ENV !== "production") console.debug("[analytics]", name, clean);
    gtag("event", name, { platform: "web", ...clean });
  } catch {
    // analytics must never break the app
  }
}

function setUser(userId) {
  try {
    if (!enabled()) return;
    init();
    gtag("set", { user_id: userId || null });
    gtag("set", "user_properties", { signed_in: userId ? "true" : "false" });
  } catch {
    // analytics must never break the app
  }
}

function setConsent(granted) {
  const next = granted ? "granted" : "denied";
  const changed = readConsent() !== next;
  try {
    localStorage.setItem(CONSENT_KEY, next);
  } catch {
    // storage unavailable: choice lasts for this page load only
  }
  if (!enabled()) return;
  init();
  gtag("consent", "update", consentArg(granted));
  if (changed) track(EVENTS.CONSENT_CHANGED, { purpose: "analytics", action: granted ? "granted" : "withdrawn" });
}

// "granted" | "denied" | null (not decided yet)
const getConsent = () => readConsent();

function pageView({ pathname, search = "", patternPath }) {
  track(EVENTS.PAGE_VIEW, {
    page_location: window.location.origin + pathname + search,
    page_path: pathname,
    page_path_pattern: patternPath || pathname,
    page_title: document.title,
    app_section: sectionForPath(pathname),
  });
}

function trackClick(elementId, params = {}) {
  track(EVENTS.UI_CLICK, {
    element_id: elementId,
    app_section: sectionForPath(window.location.pathname),
    ...params,
  });
}

export const analytics = {
  init,
  track,
  pageView,
  trackClick,
  setUser,
  setConsent,
  getConsent,
  isEnabled: enabled,
  isStaffPage,
};
export { EVENTS };
